import React, { useState } from "react";
import {
  Box,
  Container,
  Text,
  Checkbox,
  Button,
  VStack,
  useToast,
  Link,
  Divider,
  Heading,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../context/AuthContext";

const ContractAgreement = () => {
  const [agreement1, setAgreement1] = useState(false);
  const [agreement2, setAgreement2] = useState(false);
  const [agreement3, setAgreement3] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const { user, refreshUserProfile } = useAuth();

  const handleAgree = async () => {
    if (!agreement1 || !agreement2 || !agreement3) {
      toast({
        title: "모든 항목에 동의해주세요",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("user")
        .update({ is_not_contract: true })
        .eq("email", user.email);

      if (error) throw error;

      toast({
        title: "동의가 완료되었습니다",
        status: "success",
        duration: 2000,
        isClosable: true,
      });

      // 프로필 새로고침
      await refreshUserProfile();

      navigate("/");
    } catch (error) {
      toast({
        title: "오류가 발생했습니다",
        description: error.message,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReject = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from("user")
        .update({ is_not_contract: false })
        .eq("email", user.email);

      if (error) throw error;

      toast({
        title: "거절되었습니다",
        description: "서비스 이용이 어렵습니다",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });

      // 프로필 새로고침
      await refreshUserProfile();

      navigate("/report");
    } catch (error) {
      toast({
        title: "오류가 발생했습니다",
        description: error.message,
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container maxW="container.lg" py={8}>
      <Box bg="white" p={8}>
        <Heading as="h1" size="xl" mb={6} color="blackAlpha.800">
          📢 플플 음반 발매 관련 안내
        </Heading>

        <VStack spacing={4} align="stretch">
          <Text fontSize="md" lineHeight="tall">
            안녕하세요, 회원님. 플플을 이용해주셔서 진심으로 감사드립니다.
          </Text>

          <Text fontSize="md" lineHeight="tall">
            플플은 그 동안 플플의 회원이 신청하는 음반을 전 세계 DSP에 음반
            발매를 도와드렸습니다.
            <br />
            그러나, 최근 DSP 중 한 곳인 유튜브의 수익 창출 정책에 반하여 '우회
            수익 창출'에 해당하는 음반들이 확인되었습니다.
          </Text>

          <Text fontSize="md" lineHeight="tall">
            유튜브에서는 '우회 수익 창출' 정책을 꾸준히 견지하며 유통사에 정책
            준수를 요청해왔습니다. 이런 와중, 최근에 플플을 통해 음반 발매를
            하신 회원 분들의 음반 중 유튜브, 인스타그램, 틱톡 등에서 '우회 수익
            창출'에 해당하거나 잠재적으로 '우회 수익 창출'에 사용될 수 있는
            음반들이 확인되었습니다. 이에 따라 플플은 정책 준수를 위해 일부
            음반에 대해 발매 취소(Take Down)를 진행합니다.
          </Text>

          <Divider />

          <Heading as="h2" size="lg" color="orange.600" mt={4}>
            🔒 음반 발매 취소 대상
          </Heading>

          <Text fontSize="md" lineHeight="tall" as="div">
            아래의 경우에 해당될 경우, 발매가 취소됩니다:
            <br />
            <br />
            1. 허가되지 않은 영상에 음반이 사용된 경우 (불펌·짜집기 포함)
            <br />
            2. 허락을 받았더라도, 수익 창출 승인을 받지 않은 채널에서 음반이
            사용된 경우
            <br />
            3. 유튜브 커뮤니티 가이드라인을 위반한 영상에서 사용된 경우
            <br />
            4. 음원사이트에서는 사용 내역이 없고, 영상 플랫폼에서만 사용량이
            있는 경우
            <br />
            5. 유튜브 및 영상 플랫폼에서 '우회 수익 창출'로 판단한 경우
            <br />
            6. 플플에서 '우회 수익 창출'이라 판단한 경우
          </Text>

          <Divider />

          <Heading as="h2" size="lg" color="blackAlpha.800" mt={4}>
            ⚠️ 주요 공지
          </Heading>

          <Text
            fontSize="md"
            lineHeight="tall"
            fontWeight="bold"
            color="red.600"
          >
            본 공지 발송 시점부터, 위 조건에 해당하는 음반은 모든 쇼츠에서
            사용이 중단됩니다.
          </Text>

          <Text fontSize="md" lineHeight="tall" as="div">
            1. 이를 위반할 경우, 플플 운영사 ㈜루나르트는 2025년 매출의 3배수에
            해당하는 금액을 손해배상 청구할 수 있습니다.
            <br />
            2. 플플 이용자는 서비스 이용을 위해 반드시 이에 동의해야 합니다.
            <br />
            3. 플플에서 테이크다운 후에, 타 유통사를 통해 발매하시고자 하는 경우
            해당 유통사를 통해 새롭게 발매하실 수 있습니다. 관련 절차는 타
            유통사에 문의부탁드립니다.
          </Text>

          <Divider mt={4} />

          <Heading as="h2" size="lg" mt={4}>
            📩 이의 제기 안내
          </Heading>

          <Text fontSize="md" lineHeight="tall" as="div">
            위 조치에 대해 이의가 있으신 경우, 아래 구글 폼을 통해 제출해주시면
            검토 후 회신드리겠습니다.
            <br />
            <Link
              href="https://forms.gle/yaoKvNk7MB8e6G7M6"
              color="blue.500"
              isExternal
              fontWeight="bold"
            >
              👉 이의 제기 (제출)
            </Link>
            <Box mt={2}>
              <Text fontSize="md" as="span">
                - 제출 기한 : ~{" "}
              </Text>
              <Text fontSize="md" as="span" textDecoration="line-through">
                2025년 10월 30일 00:00까지 접수
              </Text>
              <Text
                fontSize="md"
                as="span"
                color="red.600"
                fontWeight="bold"
                ml={2}
              >
                2025년 11월 1일 00:00까지 접수 연장
              </Text>
            </Box>
          </Text>

          <Text fontSize="md" lineHeight="tall" mt={4}>
            플플을 이용해주셔서 다시 한번 감사말씀드리며, 모든 문의 사항은{" "}
            <Link href="mailto:contact@laeebly.com" color="blue.500">
              contact@laeebly.com
            </Link>{" "}
            으로 부탁드립니다. 감사합니다.
          </Text>

          <Text fontSize="md" lineHeight="tall" fontWeight="bold" mt={2}>
            플플 드림
          </Text>

          <Divider mt={6} />

          <Box bg="green.50" p={4} borderRadius="md" mt={4}>
            <Heading as="h2" size="md" mb={4}>
              🎵 음반 사용 허용 대상
            </Heading>

            <Text fontSize="md" lineHeight="tall" mb={2}>
              다음의 경우는 증빙 자료 제출 시 음반 사용이 허용됩니다.
            </Text>

            <Box bg="gray.100" p={2} borderRadius="md" mb={4}>
              <Text fontWeight="bold">증빙 필요:</Text> 수익 창출 승인 여부,
              직접 촬영/허가받은 영상임을 확인할 수 있는 자료
              <br />
              <Text fontWeight="bold">제출처:</Text>{" "}
              <Link
                href="https://forms.gle/WJn4w8VmSmbJGEC69"
                color="blue.500"
                isExternal
              >
                👉 증빙 제출 (클릭)
              </Link>
            </Box>

            <Text fontSize="md" lineHeight="tall" as="div">
              1. 수익 창출 승인을 받은 채널에서 직접 촬영한 영상에 사용
              <br />
              2. 수익 창출 승인을 받은 채널에서 허가받은 영상에 사용
            </Text>
          </Box>

          <Divider mt={6} />

          <Box bg="blue.50" p={4} borderRadius="md" mt={4}>
            <Heading as="h2" size="md" mb={4}>
              ✅ [필수] 동의
            </Heading>

            <VStack spacing={3} align="stretch">
              <Checkbox
                isChecked={agreement1}
                onChange={(e) => setAgreement1(e.target.checked)}
                size="lg"
                colorScheme="blue"
              >
                <Text fontSize="md" lineHeight="tall">
                  (필수) 음반 발매 취소 대상에 더 이상 플플에 등록한 음반을
                  사용하지 않는 것에 동의합니다.
                </Text>
              </Checkbox>

              <Checkbox
                isChecked={agreement2}
                onChange={(e) => setAgreement2(e.target.checked)}
                size="lg"
                colorScheme="blue"
              >
                <Text fontSize="md" lineHeight="tall">
                  (필수) 음반 발매 취소에 해당하는 경우, 음반 발매 취소(Take
                  Down)에 동의합니다.
                </Text>
              </Checkbox>

              <Checkbox
                isChecked={agreement3}
                onChange={(e) => setAgreement3(e.target.checked)}
                size="lg"
                colorScheme="blue"
              >
                <Text fontSize="md" lineHeight="tall">
                  (필수) 위 사항을 위반할 경우, 플플 운영사 (주)루나르트의
                  2025년 매출의 3배수에 해당하는 금액을 손해 배상금으로 지급할
                  것을 동의합니다.
                </Text>
              </Checkbox>
            </VStack>

            <Text fontSize="sm" color="gray.600" mt={4} fontStyle="italic">
              👉 동의하지 않을 수 있으나, 동의하지 않을 경우 플플 서비스 이용이
              어렵습니다.
            </Text>
          </Box>

          <Box display="flex" gap={4} mt={8}>
            <Button
              colorScheme="blue"
              size="lg"
              flex={1}
              onClick={handleAgree}
              isLoading={isLoading}
              isDisabled={!agreement1 || !agreement2 || !agreement3}
            >
              동의합니다
            </Button>
            <Button
              colorScheme="gray"
              size="lg"
              flex={1}
              onClick={handleReject}
              isLoading={isLoading}
            >
              거절합니다
            </Button>
          </Box>
        </VStack>
      </Box>
    </Container>
  );
};

export default ContractAgreement;
